import React, { Fragment, type FunctionComponent, useCallback, useEffect } from 'react'
import { Button, LoadingDots, spacing, useBooleanState } from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import { useDomainMessages } from 'domains/partner-pick-devices/utils/domain/intl'
import { PartnerPickDeviceListConfirmDeactivateModal } from 'domains/partner-pick-devices/components/list/components/table-cells/actions/components/PartnerPickDeviceListConfirmDeactivateModal'
import { usePartnerPickDeviceSetStatusMutation } from 'domains/partner-pick-devices/api'

interface Props {
  deviceId: string
  currentActive: Boolean
  onUpdate: () => void
}

const loadingIndicatorStyles = css({
  padding: `0 ${spacing.X52}`,
})

export const PartnerPickDeviceListSetStatusButton: FunctionComponent<Props> = ({
  deviceId,
  currentActive,
  onUpdate,
}) => {
  const messages = useDomainMessages({
    reactivate: 'partnerPickDevicesDomain.list.columns.actions.reactivate',
    deactivate: 'partnerPickDevicesDomain.list.columns.actions.deactivate',
  })

  const [triggerMutation, { data: apiData, loading: apiLoading }] =
    usePartnerPickDeviceSetStatusMutation()

  // triggers status change mutation
  const submitSetStatus = useCallback(() => {
    triggerMutation({
      variables: {
        id: deviceId,
        active: !currentActive,
      },
    })
  }, [deviceId, currentActive, triggerMutation])

  const [isModalOpen, showModal, hideModal] = useBooleanState(false)

  const onClick = useCallback(() => {
    if (currentActive) {
      // show confirmation modal before submitting active -> inactive status change
      showModal()
    } else {
      // inactive -> active state change is submitted without a confirmation step
      submitSetStatus()
    }
  }, [currentActive, showModal, submitSetStatus])

  const onDeactivateConfirm = useCallback(() => {
    hideModal()
    submitSetStatus()
  }, [submitSetStatus, hideModal])

  useEffect(() => {
    // call onUpdate when the setStatus mutation succeeds
    if (apiData) {
      onUpdate()
    }
  }, [apiData, onUpdate])

  return (
    <Fragment>
      {apiLoading ? (
        <div css={loadingIndicatorStyles} data-testid="partnerPickDeviceList-setStatus-loading">
          <LoadingDots height={10} />
        </div>
      ) : currentActive ? (
        <Button
          variant="detrimental"
          onClick={onClick}
          data-testid="partnerPickDeviceList-setStatus-deactivate"
        >
          {messages.deactivate}
        </Button>
      ) : (
        <Button
          variant="secondary"
          onClick={onClick}
          data-testid="partnerPickDeviceList-setStatus-reactivate"
        >
          {messages.reactivate}
        </Button>
      )}

      {isModalOpen && (
        <PartnerPickDeviceListConfirmDeactivateModal
          onConfirm={onDeactivateConfirm}
          onClose={hideModal}
        />
      )}
    </Fragment>
  )
}
